import { Component, computed, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { combineLatest, BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { PageShell } from '../../shared/components/page-shell/page-shell';
import { NoPermissionComponent } from '../../shared/components/no-permission/no-permission.component';
import { selectBranchId, selectPermissions, selectUserInfo, selectUserStatus } from '../../core/store/auth/auth.selectors';
import { DashboardService } from '../../core/services/dashboard.service';
import { DoctorDashboard, OwnerDashboard, OwnerFinanceDashboard, StaffDashboard, StatusBreakdown, StatusPeriod } from '../../core/models/clinic-dashboard.model';
import { DashOwnerView } from './views/owner-view';
import { DashDoctorView } from './views/doctor-view';
import { DashStaffView } from './views/staff-view';

type DashboardRole = 'owner' | 'doctor' | 'staff';

// users_tb.user_type_id (user_types_tb): 2 Owner, 3 Médico, everything else is front-desk/staff.
const USER_TYPE_OWNER = 2;
const USER_TYPE_DOCTOR = 3;

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, PageShell, TranslateModule, NoPermissionComponent, DashOwnerView, DashDoctorView, DashStaffView],
  templateUrl: './dashboard.html',
})
export class Dashboard implements OnInit {
  private store = inject(Store);
  private dashboardService = inject(DashboardService);
  private destroyRef = inject(DestroyRef);

  private permissions$ = this.store.select(selectPermissions);
  private userStatus$ = this.store.select(selectUserStatus);
  private userInfo$ = this.store.select(selectUserInfo);

  canAccessDashboard$: Observable<boolean> = combineLatest([this.permissions$, this.userStatus$]).pipe(
    map(([permissions, status]) => status === 'all' || (permissions ? permissions.includes('Dashboard') : false)),
  );

  onlineUsers$ = new BehaviorSubject<number | null>(null);

  readonly role = signal<DashboardRole | null>(null);
  readonly owner = signal<OwnerDashboard | null>(null);
  readonly ownerFinance = signal<OwnerFinanceDashboard | null>(null);
  /** Period filter of the appointment-status chart. 'today' uses the owner payload as is. */
  readonly statusPeriod = signal<StatusPeriod>('today');
  readonly statusBreakdown = signal<StatusBreakdown | null>(null);
  readonly statusLoading = signal(false);
  readonly doctor = signal<DoctorDashboard | null>(null);
  readonly staff = signal<StaffDashboard | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);

  /** Which view to render: only once that role's payload has actually loaded. */
  readonly view = computed<DashboardRole | null>(() => {
    switch (this.role()) {
      case 'owner': return this.owner() ? 'owner' : null;
      case 'doctor': return this.doctor() ? 'doctor' : null;
      case 'staff': return this.staff() ? 'staff' : null;
      default: return null;
    }
  });
  readonly hasData = computed(() => this.view() !== null);

  private role$: Observable<DashboardRole> = combineLatest([this.userInfo$, this.userStatus$]).pipe(
    map(([info, status]): DashboardRole | null => {
      if (!info && status !== 'all') return null;
      const type = Number(info?.user_type_id);
      if (status === 'all' || type === USER_TYPE_OWNER) return 'owner';
      if (type === USER_TYPE_DOCTOR) return 'doctor';
      return 'staff';
    }),
    filter((r): r is DashboardRole => r !== null),
    distinctUntilChanged(),
  );

  private branchId$ = this.store.select(selectBranchId).pipe(filter(id => !!id), distinctUntilChanged());

  ngOnInit() {
    this.role$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(r => this.role.set(r));

    // Reload when the branch (clinic switcher) or the resolved role changes.
    combineLatest([this.branchId$, this.role$]).pipe(
      switchMap(([branchId, role]) => this.canAccessDashboard$.pipe(
        take(1),
        filter(can => can),
        map(() => ({ branchId: branchId!, role })),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ branchId, role }) => {
      this.current = { branchId, role };
      void this.load(branchId, role);
    });

    // Presence count is an owner-only card.
    combineLatest([this.branchId$, this.role$]).pipe(
      filter(([, role]) => role === 'owner'),
      switchMap(([branchId]) => this.dashboardService.getOnlineUsers(branchId!)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(n => this.onlineUsers$.next(n));
  }

  private current: { branchId: number; role: DashboardRole } | null = null;

  async onStatusPeriodChange(period: StatusPeriod) {
    this.statusPeriod.set(period);
    if (this.current) await this.loadStatusBreakdown(this.current.branchId);
  }

  private async loadStatusBreakdown(branchId: number) {
    const period = this.statusPeriod();
    if (period === 'today') {
      this.statusBreakdown.set(null);
      return;
    }
    this.statusLoading.set(true);
    try {
      const { data, error } = await this.dashboardService.getStatusBreakdown(branchId, period);
      if (error) throw error;
      // Ignore a late answer if the user has already picked another period.
      if (this.statusPeriod() === period) this.statusBreakdown.set(data as StatusBreakdown);
    } catch (e) {
      console.error('Error loading status breakdown:', e);
      this.statusBreakdown.set(null);
    } finally {
      this.statusLoading.set(false);
    }
  }

  /** Re-fetch in place: keeps the current data on screen until the new payload lands. */
  refresh() {
    if (this.loading()) return;
    if (this.current) void this.load(this.current.branchId, this.current.role, false);
  }

  private async load(branchId: number, role: DashboardRole, reset = true) {
    this.loading.set(true);
    this.error.set(false);
    if (reset) {
      // Drop the previous branch's data so it never shows under the new one.
      this.owner.set(null);
      this.ownerFinance.set(null);
      this.doctor.set(null);
      this.staff.set(null);
    }
    try {
      if (role === 'owner') {
        // Finance is a separate, owner-only call; if it is refused or fails the rest still shows.
        const [main, fin] = await Promise.all([
          this.dashboardService.getOwnerDashboard(branchId),
          this.dashboardService.getOwnerFinance(branchId),
        ]);
        if (main.error) throw main.error;
        this.owner.set(main.data as OwnerDashboard);
        this.ownerFinance.set(fin.error ? null : (fin.data as OwnerFinanceDashboard));
        await this.loadStatusBreakdown(branchId);
      } else if (role === 'doctor') {
        const { data, error } = await this.dashboardService.getDoctorDashboard(branchId);
        if (error) throw error;
        this.doctor.set(data as DoctorDashboard);
      } else {
        const { data, error } = await this.dashboardService.getStaffDashboard(branchId);
        if (error) throw error;
        this.staff.set(data as StaffDashboard);
      }
    } catch (e) {
      console.error('Error loading dashboard data:', e);
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
